<template>
  <div class="dropdown" @click.self="closeIfOpen">
    <button class="btn p-0 border-0 dropdown-toggle"
            :title="nomeExibido" style="box-shadow: none;"
            @click.prevent="toggleOpen">
      <div v-if="urlFotoInterna" class="user-avatar d-flex align-items-center justify-content-center fw-bold text-white"
           :style="{ width: '36px', height: '36px', borderRadius: '50%', overflow: 'hidden', cursor: 'pointer' }">
        <img :src="urlFotoInterna" alt="Avatar" style="width:36px;height:36px;object-fit:cover;" />
      </div>
      <div v-else class="user-avatar d-flex align-items-center justify-content-center fw-bold text-white"
           :style="{ backgroundColor: avatarColor, width: '36px', height: '36px', borderRadius: '50%', fontSize: '1rem', cursor: 'pointer' }">
        {{ avatarInitial }}
      </div>
    </button>

    <ul v-if="isOpen" class="dropdown-menu dropdown-menu-dark dropdown-menu-end shadow-lg show"
        style="min-width: 220px; position: absolute; right: 0; top: 100%;">
      <!-- Header -->
      <li>
        <span class="dropdown-item-text d-flex align-items-center gap-2">
          <div v-if="urlFotoInterna" class="user-avatar-sm d-flex align-items-center justify-content-center flex-shrink-0"
               style="width:32px;height:32px;border-radius:50%;overflow:hidden;">
            <img :src="urlFotoInterna" alt="Avatar" style="width:32px;height:32px;object-fit:cover;" />
          </div>
          <div v-else class="user-avatar-sm d-flex align-items-center justify-content-center fw-bold text-white flex-shrink-0"
               :style="{ backgroundColor: avatarColor, width: '32px', height: '32px', borderRadius: '50%', fontSize: '0.85rem' }">
            {{ avatarInitial }}
          </div>
          <span class="d-flex flex-column">
            <strong class="text-white" style="font-size: 0.9rem;">
              {{ nomeExibido }}
              <i class="bi bi-gear ms-1" style="font-size: 0.75rem; cursor: pointer;" @click.stop="abrirMeuPerfil" title="Meu Perfil"></i>
            </strong>
            <small v-if="isAdminInterno" class="text-white-50">Administrador Interno</small>
            <small v-else-if="isAdminCliente" class="text-white-50">Administrador</small>
          </span>
        </span>
      </li>

      <li><hr class="dropdown-divider"></li>

      <!-- Meu Perfil -->
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="abrirMeuPerfil">
          <i class="bi bi-person-gear" style="font-size: 1rem;"></i>
          <span>Meu Perfil</span>
        </a>
      </li>

      <!-- Alterar Senha -->
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="alterarSenha">
          <i class="bi bi-lock" style="font-size: 1rem;"></i>
          <span>Alterar Senha</span>
        </a>
      </li>

      <li><hr class="dropdown-divider"></li>

      <!-- Tema -->
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="toggleTema">
          <i class="bi" :class="isDarkMode ? 'bi-moon-stars-fill' : 'bi-sun-fill'" style="font-size: 1rem;"></i>
          <span>{{ isDarkMode ? 'Tema Escuro' : 'Tema Claro' }}</span>
        </a>
      </li>

      <!-- Zoom Padrão -->
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="toggleZoom">
          <i class="bi bi-zoom-in" style="font-size: 1rem;"></i>
          <span>Zoom Padrão</span>
          <span class="ms-auto badge bg-secondary-subtle text-secondary-emphasis" style="font-size: 0.7rem;">{{ zoomAtual }}%</span>
        </a>
        <ul v-if="isZoomOpen" class="zoom-submenu list-unstyled mb-0">
          <li v-for="opcao in zoomOpcoes" :key="opcao">
            <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="aplicarZoom(opcao)">
              <i class="bi" :class="opcao === zoomAtual ? 'bi-check-circle-fill text-success' : 'bi-circle'" style="font-size: 0.9rem;"></i>
              <span>{{ opcao }}%</span>
            </a>
          </li>
          <li><hr class="dropdown-divider my-1"></li>
          <li>
            <div class="d-flex align-items-center gap-1 px-3 py-1">
              <input
                type="number"
                class="form-control form-control-sm zoom-custom-input"
                v-model.number="zoomCustomValue"
                min="50"
                max="150"
                placeholder="%"
                @keydown.enter.prevent="aplicarZoomCustom"
              />
              <button class="btn btn-sm btn-info text-white zoom-custom-btn" @click="aplicarZoomCustom" title="Aplicar">
                <i class="bi bi-check-lg"></i>
              </button>
            </div>
          </li>
        </ul>
      </li>

      <li><hr class="dropdown-divider"></li>

      <!-- Organização (multi-organização) — drilldown no padrão do Zoom -->
      <li v-if="listaOrganizacoes.length > 0">
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="toggleOrganizacao">
          <i class="bi bi-diagram-3 menu-icon-muted" style="font-size: 1rem;"></i>
          <span>Organização</span>
          <span class="ms-auto badge badge-menu-fixed">{{ organizacaoAtualNome || '—' }}</span>
        </a>
        <ul v-if="isOrganizacaoOpen" class="zoom-submenu list-unstyled mb-0">
          <li v-for="org in listaOrganizacoes" :key="org.idOrganizacao">
            <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="selecionarOrganizacao(org.idOrganizacao)">
              <i class="bi" :class="org.idOrganizacao === organizacaoAtualId ? 'bi-check-circle-fill text-success' : 'bi-circle'" style="font-size: 0.9rem;"></i>
              <span>{{ org.nomeFantasia || org.razaoSocial }}</span>
            </a>
          </li>
        </ul>
      </li>

      <li><hr class="dropdown-divider"></li>

      <!-- Helper: assistente de captação (mesmo design do botão do header — ghost + ícone ciano) -->
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="abrirNovaSugestao">
          <i class="bi bi-robot menu-icon-ia" style="font-size: 1rem;"></i>
          <span class="fw-medium">Helper</span>
          <span class="badge ms-auto badge-menu-ai">AI</span>
        </a>
      </li>

      <!-- Sugestões e Dúvidas (mesmo design do botão do header — ghost + ícone roxo) -->
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="abrirGestaoSugestoes">
          <i class="bi bi-lightbulb-fill menu-icon-sugestoes" style="font-size: 1rem;"></i>
          <span class="fw-medium">Sugestões e Dúvidas</span>
        </a>
      </li>

      <!-- Histórico de Notificações (apenas mobile) -->
      <li v-if="!isDesktop">
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="abrirHistoricoNotificacoes">
          <i class="bi bi-clock-history" style="font-size: 1rem;"></i>
          <span>Histórico de Notificações</span>
        </a>
      </li>

      <li><hr class="dropdown-divider"></li>

      <!-- Inicialização Cliente (apenas Admin Interno) -->
      <li v-if="isAdminInterno">
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="abrirInicializacaoCliente">
          <i class="bi bi-activity text-danger" style="font-size: 1rem;"></i>
          <span>Inicialização Cliente</span>
          <span class="ms-auto badge bg-danger-subtle text-danger-emphasis" style="font-size: 0.6rem;">seed</span>
        </a>
      </li>

      <!-- Sobre o Sistema -->
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2" href="#" @click.prevent="abrirSobre">
          <i class="bi bi-info-circle" style="font-size: 1rem;"></i>
          <span>Sobre o Sistema</span>
          <span v-if="releaseName" class="ms-auto badge bg-secondary-subtle text-secondary-emphasis" style="font-size: 0.7rem;">{{ releaseName }}</span>
        </a>
      </li>

      <li><hr class="dropdown-divider"></li>

      <!-- Sair -->
      <li>
        <a class="dropdown-item d-flex align-items-center gap-2 text-danger" href="#" @click.prevent="sair">
          <i class="bi bi-box-arrow-right" style="font-size: 1rem;"></i>
          <span>Sair</span>
        </a>
      </li>
    </ul>
  </div>

</template>

<script>
export default {
  name: 'AvatarMenu',
  props: {
    nomeUtilizador: { type: String, default: '?' },
    nomeExibicao: { type: String, default: '' },
    urlFotoInterna: { type: String, default: '' },
    isAdminInterno: { type: Boolean, default: false },
    isAdminCliente: { type: Boolean, default: false },
    isDarkMode: { type: Boolean, default: false },
    isDesktop: { type: Boolean, default: true },
    zoomAtual: { type: Number, default: 80 },
    zoomOpcoes: { type: Array, default: () => [60, 70, 80, 90, 100] },
    releaseName: { type: String, default: '' },
    buildTime: { type: String, default: '' },
    listaOrganizacoes: { type: Array, default: () => [] },
    organizacaoAtualId: { type: Number, default: null }
  },
  emits: ['toggle-tema', 'abrir-sobre', 'alterar-senha', 'sair', 'abrir-historico-notificacoes', 'abrir-gestao-sugestoes', 'abrir-nova-sugestao', 'abrir-meu-perfil', 'aplicar-zoom', 'abrir-inicializacao-cliente', 'selecionar-organizacao'],
  data () {
    return {
      isOpen: false,
      isZoomOpen: false,
      isOrganizacaoOpen: false,
      zoomCustomValue: 80
    }
  },
  computed: {
    nomeExibido () {
      return this.nomeExibicao || this.nomeUtilizador
    },
    organizacaoAtualNome () {
      if (!this.organizacaoAtualId) return null
      const found = this.listaOrganizacoes.find(o => o.idOrganizacao === this.organizacaoAtualId)
      return found ? (found.nomeFantasia || found.razaoSocial) : null
    },
    avatarInitial () {
      return this.nomeExibido?.charAt(0)?.toUpperCase() || '?'
    },
    avatarColor () {
      const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#16a085']
      let hash = 0
      const name = this.nomeExibido || '?'
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      return colors[Math.abs(hash) % colors.length]
    }
  },
  methods: {
    toggleOpen () {
      this.isOpen = !this.isOpen
      if (!this.isOpen) {
        this.isZoomOpen = false
        this.isOrganizacaoOpen = false
      }
    },
    closeIfOpen () {
      if (this.isOpen) {
        this.isOpen = false
        this.isZoomOpen = false
        this.isOrganizacaoOpen = false
      }
    },
    toggleZoom () {
      this.isZoomOpen = !this.isZoomOpen
      if (this.isZoomOpen) {
        this.isOrganizacaoOpen = false
        this.zoomCustomValue = this.zoomAtual
      }
    },
    toggleOrganizacao () {
      this.isOrganizacaoOpen = !this.isOrganizacaoOpen
      if (this.isOrganizacaoOpen) {
        this.isZoomOpen = false
      }
    },
    selecionarOrganizacao (id) {
      this.isOrganizacaoOpen = false
      this.isOpen = false
      this.$emit('selecionar-organizacao', id)
    },
    aplicarZoom (pct) {
      this.isZoomOpen = false
      this.isOpen = false
      this.$emit('aplicar-zoom', pct)
    },
    aplicarZoomCustom () {
      const pct = Number(this.zoomCustomValue)
      if (pct >= 50 && pct <= 150 && !isNaN(pct)) {
        this.isZoomOpen = false
        this.isOpen = false
        this.$emit('aplicar-zoom', pct)
      }
    },
    abrirMeuPerfil () { this.isOpen = false; this.$emit('abrir-meu-perfil') },
    toggleTema () { this.isOpen = false; this.$emit('toggle-tema') },
    abrirInicializacaoCliente () { this.isOpen = false; this.$emit('abrir-inicializacao-cliente') },
    abrirSobre () { this.isOpen = false; this.$emit('abrir-sobre') },
    alterarSenha () { this.isOpen = false; this.$emit('alterar-senha') },
    abrirHistoricoNotificacoes () { this.isOpen = false; this.$emit('abrir-historico-notificacoes') },
    abrirGestaoSugestoes () { this.isOpen = false; this.$emit('abrir-gestao-sugestoes') },
    abrirNovaSugestao () { this.isOpen = false; this.$emit('abrir-nova-sugestao') },
    sair () { this.isOpen = false; this.$emit('sair') }
  },
  mounted () {
    const close = (e) => {
      if (!this.$el.contains(e.target)) {
        this.isOpen = false
      }
    }
    document.addEventListener('click', close)
    this._closeHandler = close
  },
  beforeUnmount () {
    document.removeEventListener('click', this._closeHandler)
  }
}
</script>

<style scoped>
.dropdown-toggle::after {
  display: none !important;
}
.dropdown {
  position: relative;
}
.zoom-submenu {
  margin-left: 1.25rem;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  padding-left: 0.25rem !important;
}
.zoom-submenu .dropdown-item {
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
}
/* Ícones coloridos carregam a identidade (mesmo padrão ghost do header) */
.menu-icon-ia {
  color: #22D3EE !important;
}
.menu-icon-sugestoes {
  color: #8B5CF6 !important;
}
.menu-icon-muted {
  color: #C7D0DA !important;
}
/* Badge "AI": fill sólido + texto escuro */
.badge-menu-ai {
  font-size: 0.6rem;
  font-weight: 700;
  background-color: #22D3EE !important;
  color: #083344 !important;
}
/* Badge neutro fixo (não depende do tema) */
.badge-menu-fixed {
  font-size: 0.7rem;
  font-weight: 600;
  background-color: rgba(255, 255, 255, 0.12) !important;
  color: #E6EAEE !important;
}
.zoom-custom-input {
  width: 70px !important;
  min-width: 70px;
  height: 28px;
  font-size: 0.8rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  text-align: center;
}
.zoom-custom-input:focus {
  border-color: var(--bs-info);
  background-color: rgba(255, 255, 255, 0.15);
  color: #fff;
  box-shadow: none;
}
.zoom-custom-btn {
  height: 28px;
  padding: 0 0.5rem;
  font-size: 0.75rem;
  line-height: 1;
}
.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  border-radius: 0.25rem;
  margin: 0.1rem 0.3rem;
  transition: background-color 0.15s ease;
  cursor: pointer;
}
.dropdown-item:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}
.dropdown-item.text-danger:hover {
  background-color: rgba(220, 53, 69, 0.15) !important;
}
</style>
